/*
  <badge-group> wraps a .badges container and, for any child .badge whose
  text wraps onto more than one line, pins that badge's width to its
  longest rendered line (removing it first so the measurement reflects the
  browser's own natural per-line wrap, not a stale fixed width from an
  earlier pass). Combined with the .is-fitted CSS in critical.css, this
  turns a wrapped badge from several different-width lines into one flush
  rectangle -- see docs/assets/critical.md for why that can't be done in
  CSS alone.

  Single-line badges are left untouched: the default per-line CSS already
  renders them correctly, and it is also what every badge looks like
  before this script has run, so nothing pops in.

  Watches the PARENT element's size, not its own: once a badge is pinned to
  a fixed width, badge-group's own box stops growing/shrinking with the
  available space (it is now driven by that fixed width instead), so a
  ResizeObserver watching badge-group itself would go stale exactly when it
  matters -- e.g. never noticing extra room appeared and the badge could go
  back to one line. The parent (card-media-overlay on cards, media-sliders
  on the product page) sizes itself independently of badge-group's content
  in every place this renders, so it keeps reporting the true available
  width. See docs/assets/critical.md for how this was found.

  Re-measures on a `badge:textchange` event too, for the one place badge
  text changes without necessarily changing the available width:
  updateSaleBadge() in template-product.js, when a shopper switches variant.
*/
if (!customElements.get('badge-group')) {
  customElements.define('badge-group', class BadgeGroup extends HTMLElement {
    connectedCallback() {
      this.measure = this.measure.bind(this);
      this.measure();

      if (this.parentElement) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.parentElement);
      }

      this.addEventListener('badge:textchange', this.measure);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.removeEventListener('badge:textchange', this.measure);
    }

    measure() {
      this.querySelectorAll(':scope > .badge').forEach((badge) => {
        const text = badge.querySelector('.badge-text');
        if (!text) return;

        // Reset to the natural, per-line-hugging layout before measuring,
        // so a fixed width left over from a previous pass (e.g. before a
        // resize made the badge narrower) can't affect this measurement.
        badge.style.removeProperty('width');
        badge.classList.remove('is-fitted');

        const lineRects = text.getClientRects();
        if (lineRects.length <= 1) return;

        let maxLineWidth = 0;
        for (const rect of lineRects) {
          if (rect.width > maxLineWidth) maxLineWidth = rect.width;
        }

        badge.style.width = `${Math.ceil(maxLineWidth)}px`;
        badge.classList.add('is-fitted');
      });
    }
  });
}
